import React, { useState, useEffect, useCallback } from 'react';
import './home.css'
import * as XLSX from 'xlsx';
import { toast } from 'sonner';
import Cookies from 'js-cookie';
import { ClipLoader } from "react-spinners";
import { useTranslation } from 'react-i18next';
import { formatInTimeZone } from 'date-fns-tz';
import { getHomeEnergy, getHomeGridPoint, getHomeIncident, getStatus } from '../../services/requests';
import { HomeGridPoint, Incident, Status } from '../../interfaces/interfaces';
import { useTheme } from '../../themeContext';
import { ChartOptionsBuilder } from '../../components/graphics/chartOptions';
import ReactECharts from 'echarts-for-react';

const HomeComponent: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [date, setDate] = useState("")
    const [energy, setEnergy] = useState("");
    const [energyDescrip, setEnergyDescrip] = useState("");
    const [activeConnections, setActiveConnections] = useState("");
    const [activeConnectionsDescrip, setActiveConnectionsDescrip] = useState("");
    const [openIncidents, setOpenIncidents] = useState("");
    const [openIncidentsDescrip, setOpenIncidentsDescrip] = useState("");
    const [emissions, setEmissions] = useState("");
    const [emissionsDescrip, setEmissionsDescrip] = useState("");
    const [barData, setBarData] = useState<number[]>([]);
    const [barCategories, setBarCategories] = useState<string[]>([]);
    const [cakeData, setCakeData] = useState<number[]>([]);
    const [cakeCategories, setCakeCategories] = useState<string[]>([]);
    const [dataGridPoint, setDataGridPoint] = useState<{ ID: string; Name: string; Status: string; Status2: number; ConnectedVessel: string }[]>([{ ID: "", Name: "", Status: "", Status2: 0, ConnectedVessel: "" }]);
    const [dataIncidentsOpen, setDataIncidentsOpen] = useState([{ ID: "", Type: 0, Description: "", Status: "", Date: "" }]);
    const [dataIncidentsClose, setDataIncidentsClose] = useState([{ ID: "", Type: 0, Description: "", Status: "", Date: "" }]);
    const { t } = useTranslation();

    /**
     * Convierte un string en formato ISO a una cadena con formato "YYYY-MM-DD HH:MM:SS".
     * @param isoString - La cadena de fecha en formato ISO.
     * @returns La fecha formateada como string legible.
     */
    const formatDate = (isoString: string): string => {
        const timeZone = 'Europe/Madrid';
        return formatInTimeZone(isoString, timeZone, 'yyyy-MM-dd HH:mm:ss');
    };

    /**
     * Obtiene información desde el endpoint `/home/`, incluyendo métricas energéticas,
     * datos de gráficas y estado de incidentes. Formatea y actualiza múltiples estados del componente.
     * @remarks Esta función está memorizada usando `useCallback` y se ejecuta al montar el componente.
     */
    const getInformation = useCallback(async () => {
        try {
            const responseHomeIncident = await getHomeIncident();

            const responseHomeEnergy = await getHomeEnergy();

            const responseHomeGridPoint = await getHomeGridPoint();

            const allOk = [responseHomeIncident, responseHomeEnergy, responseHomeGridPoint]
                .every(res => res.status >= 200 && res.status < 300);

            if (allOk) {
                toast.success(
                    `${responseHomeIncident.data.message}, ${responseHomeEnergy.data.message}, ${responseHomeGridPoint.data.message}`
                );
            }

            const responseStatus = await getStatus();

            const dateTemp = formatDate(responseHomeEnergy.data.data.date.toString())

            setDate(dateTemp)
            Cookies.set('date', dateTemp, { expires: 7 });
            setEnergy(responseHomeEnergy.data.data.energy.toFixed(2));
            setEnergyDescrip(responseHomeEnergy.data.data.energyDescrip.toFixed(2));
            setActiveConnections(responseHomeEnergy.data.data.activeConnections.toString());
            setActiveConnectionsDescrip(responseHomeEnergy.data.data.activeConnectionsDescrip.toString());
            setOpenIncidents(responseHomeIncident.data.data.openIncidents.toString());
            setOpenIncidentsDescrip(responseHomeIncident.data.data.openIncidentsDescrip.toString());
            setEmissions(responseHomeEnergy.data.data.emissions.toString());
            setEmissionsDescrip(responseHomeEnergy.data.data.emissionsDescrip.toString());
            setBarData(responseHomeEnergy.data.data.barData);
            setBarCategories(responseHomeEnergy.data.data.barCategories);
            setCakeData(responseHomeEnergy.data.data.cakeData);
            setCakeCategories(responseHomeEnergy.data.data.cakeCategories);

            const transformedData = (responseHomeGridPoint.data.data.gridStatus as HomeGridPoint["gridStatus"][])
                .map(gridStatus => {
                    const statusMatch = (responseStatus.data.data as Status[]).find(item => item.Testado === gridStatus.Status);

                    return {
                        ID: gridStatus.ID.toString(),
                        Name: gridStatus.Name.toString(),
                        Status: statusMatch ? statusMatch.Testado : "",
                        Status2: statusMatch ? statusMatch.Nestado : 0,
                        ConnectedVessel: gridStatus.ConnectedVessel.toString(),
                    };
                });

            setDataGridPoint(transformedData);

            const openIncidents = (responseHomeIncident.data.data.incidentStatus.open as Incident[]).map((incident) => ({
                ID: incident.ID,
                Type: incident.Type,
                Description: incident.Description,
                Status: incident.Status,
                Date: incident.Date,
            }));

            setDataIncidentsOpen(openIncidents);

            const closeIncidents = (responseHomeIncident.data.data.incidentStatus.close as Incident[]).map((incident) => ({
                ID: incident.ID,
                Type: incident.Type,
                Description: incident.Description,
                Status: incident.Status,
                Date: incident.Date,
            }));

            setDataIncidentsClose(closeIncidents);

        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || error;
            toast.error(`${errorMessage}`);
            setLoading(false);
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Ejecuta la función `getInformation` al montar el componente
     * o cuando cambien las dependencias de `getInformation`.
     */
    useEffect(() => {
        getInformation();
    }, [getInformation]);

    const [isFullScreenGraphicBars, setIsFullScreenGraphicBars] = useState(false);
    const [isFullScreenGraphicCake, setIsFullScreenGraphicCake] = useState(false);
    const [isFullScreenTable, setIsFullScreenTable] = useState(false);

    /**
     * Genera y descarga un archivo Excel (.xlsx) con los datos de una gráfica.
     * @param categories - Array de nombres de categorías (eje X).
     * @param data - Array de valores correspondientes a cada categoría.
     * @param title - Nombre del archivo y título de la hoja Excel.
     */
    const downloadDataAsXLSX = (categories: string[], data: number[], title: string) => {
        const worksheetData = categories.map((category, index) => ({
            Category: category,
            Value: data[index],
        }));
        const worksheet = XLSX.utils.json_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, title);
        XLSX.writeFile(workbook, `${title}.xlsx`);
    };

    const [sortConfig, setSortConfig] = useState<{
        key: keyof typeof dataGridPoint[0] | null;
        direction: "asc" | "desc" | null;
    }>({ key: "Name", direction: "asc" });

    /**
     * Efecto que ordena inicialmente `dataGridPoint` por el nombre (`Name`) en orden ascendente.
     */
    useEffect(() => {
        const sorted = [...dataGridPoint].sort((a, b) => a.Name.localeCompare(b.Name));
        setSortedData(sorted);
    }, [dataGridPoint]);

    /**
     * Ordena los datos de la tabla por una clave específica (`key`) y alterna entre orden ascendente y descendente.
     * @param key - La propiedad del objeto por la cual se va a ordenar.
     */
    const sortData = (key: keyof typeof dataGridPoint[0]) => {
        let direction: "asc" | "desc" = "asc";
        if (sortConfig.key === key && sortConfig.direction === "asc") {
            direction = "desc";
        }
        setSortConfig({ key, direction });

        const sorted = [...sortedData].sort((a, b) => {
            if (a[key] < b[key]) return direction === "asc" ? -1 : 1;
            if (a[key] > b[key]) return direction === "asc" ? 1 : -1;
            return 0;
        });

        setSortedData(sorted);
    };

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);
    const [sortedData, setSortedData] = useState([...dataGridPoint]);
    const totalPage = itemsPerPage === sortedData.length ? 1 : Math.ceil(sortedData.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = itemsPerPage === sortedData.length ? sortedData.length : startIndex + itemsPerPage;
    const currentData = sortedData.slice(startIndex, endIndex);

    /**
     * Avanza a la siguiente página si no se ha llegado al final.
     */
    const handleNextPage = () => {
        if (currentPage < totalPage) {
            setCurrentPage(currentPage + 1);
        }
    };

    /**
     * Retrocede a la página anterior si no se está en la primera.
     */
    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const [showResolved, setShowResolved] = useState(false);

    /**
     * Descarga la tabla completa de `dataGridPoint` en formato Excel.
     * Cada fila representa un punto de la red.
     */
    const downloadDataTableAsXLSX = () => {
        const sanitizedData = dataGridPoint.map(({ Status2, ...rest }) => rest);
        const worksheet = XLSX.utils.json_to_sheet(sanitizedData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Data Grid Point');
        XLSX.writeFile(workbook, 'Data Grid Point.xlsx');
    };

    const { isDarkTheme } = useTheme();

    /**
     * Construye las opciones para un gráfico de barras utilizando `ChartOptionsBuilder`.
     * - Usa `createBarChartOption1` con los datos de energía (`barData`) y las categorías (`barCategories`).
     * - El título del eje Y se obtiene con la traducción `home.energyConsumption` seguido de `(kWh)`.
     * - Se adapta al tema claro/oscuro con `isDarkTheme`.
     */
    const optionsBar = ChartOptionsBuilder.createBarChartOption1(barData, barCategories, [`${t('home.energyConsumption')} (kWh)`], isDarkTheme);

    /**
     * Construye las opciones para un gráfico tipo pastel (torta) usando `ChartOptionsBuilder`.
     * - Utiliza `createCakeChartOption` con los datos (`cakeData`) y las categorías (`cakeCategories`).
     * - Se adapta al tema claro/oscuro mediante la variable `isDarkTheme`.
     */
    const optionsCake = ChartOptionsBuilder.createCakeChartOption(cakeData, cakeCategories, isDarkTheme);

    return (
        <div className='containerHome'>
            {loading ? (
                <div className="loading">
                    <ClipLoader color="#007bff" size={50} />
                    <p>{t('home.loadingInformation')}</p>
                </div>
            ) : (
                <>
                    <div className='title'>
                        <h4>{t('home.dashboard')}</h4>
                        <h6>{date}</h6>
                    </div>
                    <div className='separator1'>
                        <div className='card'>
                            <div>
                                <span>{t('home.totalEnergyConsumption')}</span>
                                <span className='icon'><i className="fas fa-bolt"></i></span>
                            </div>
                            <span>{energy} kWh</span>
                            <span>{energyDescrip}% {t('home.fromLastMonth')}</span>
                        </div>

                        <div className='card'>
                            <div>
                                <span>{t('home.activeConnections')}</span>
                                <span className='icon'><i className="fas fa-anchor"></i></span>
                            </div>
                            <span>{activeConnections}</span>
                            <span>{activeConnectionsDescrip} {t('home.moreThenYesterday')}</span>
                        </div>

                        <div className='card'>
                            <div>
                                <span>{t('home.openIncidents')}</span>
                                <span className='icon'><i className="fas fa-exclamation-triangle"></i></span>
                            </div>
                            <span>{openIncidents}</span>
                            <span>{openIncidentsDescrip} {t('home.resolverInLast24h')}</span>
                        </div>

                        <div className='card'>
                            <div>
                                <span>{t('home.CO2EmissionsSaved')}</span>
                                <span className='icon'><i className="fas fa-leaf"></i></span>
                            </div>
                            <span>{emissions} kg</span>
                            <span>{emissionsDescrip}% {t('home.increaseFromLastWeek')}</span>
                        </div>
                    </div>

                    <div className='separator2'>
                        <div className={`card ${isFullScreenGraphicBars ? 'expanded' : ''}`}>
                            <div className='caption'>
                                <span className='title'>{t('home.energyConsumption')}</span>
                                <div className='icons'>
                                    <i
                                        className={`fas ${isFullScreenGraphicBars ? 'fa-compress' : 'fa-expand'}`}
                                        onClick={() => setIsFullScreenGraphicBars(!isFullScreenGraphicBars)}
                                        title={isFullScreenGraphicBars ? "Salir de Pantalla Completa" : "Pantalla Completa"}
                                    ></i>
                                    <i
                                        className="fas fa-file-excel"
                                        onClick={() => downloadDataAsXLSX(barCategories, barData, 'Energy Consumption')}
                                        title="Descargar como Excel"
                                    ></i>
                                </div>
                            </div>
                            <div className='graphic'>
                                <div style={{ height: '100%', width: '100%' }}>
                                    <ReactECharts
                                        option={optionsBar}
                                        style={{ height: '100%', width: '99.9%' }}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className={`card ${isFullScreenGraphicCake ? 'expanded' : ''}`}>
                            <div className='caption'>
                                <span className='title'>{t('home.CO2EmissionsSaved')}</span>
                                <div className='icons'>
                                    <i
                                        className={`fas ${isFullScreenGraphicCake ? 'fa-compress' : 'fa-expand'}`}
                                        onClick={() => setIsFullScreenGraphicCake(!isFullScreenGraphicCake)}
                                        title={isFullScreenGraphicCake ? "Salir de Pantalla Completa" : "Pantalla Completa"}
                                    ></i>
                                    <i
                                        className="fas fa-file-excel"
                                        onClick={() => downloadDataAsXLSX(cakeCategories, cakeData, 'CO2 Emissions Saved')}
                                        title="Descargar como Excel"
                                    ></i>
                                </div>
                            </div>
                            <div className='graphic'>
                                <div style={{ height: '100%', width: '100%' }}>
                                    <ReactECharts
                                        option={optionsCake}
                                        style={{ height: '100%', width: '99.9%' }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='separator2'>
                        <div className={`card ${isFullScreenTable ? 'expanded' : ''}`}>
                            <div className='caption'>
                                <span className='title'>{t('home.activeGridPoints')}</span>
                                <div className='right'>
                                    <button className="btn btn-dark download-btn" onClick={() => downloadDataTableAsXLSX()}>
                                        <i className="fas fa-download"></i>
                                    </button>

                                    <button className="btn btn-dark fullscreen-btn" onClick={() => {
                                        setIsFullScreenTable(!isFullScreenTable);
                                    }}>
                                        <i className={`fas ${isFullScreenTable ? 'fa-compress' : 'fa-expand'}`}></i>
                                    </button>
                                </div>
                            </div>
                            <div className='tableReports'>
                                <table className="table table-hover">
                                    <thead>
                                        <tr>
                                            <th onClick={() => sortData("ID")}>
                                                ID {sortConfig.key === "ID" ?
                                                    (sortConfig.direction === "asc" ?
                                                        (<i className="fas fa-arrow-up" />) :
                                                        (<i className="fas fa-arrow-down" />)
                                                    ) : (
                                                        <i className="fa-solid fa-arrow-down-a-z"></i>
                                                    )}
                                            </th>
                                            <th onClick={() => sortData("Name")}>
                                                {t('home.name')} {sortConfig.key === "Name" ?
                                                    (sortConfig.direction === "asc" ?
                                                        (<i className="fas fa-arrow-up" />) :
                                                        (<i className="fas fa-arrow-down" />)
                                                    ) : (
                                                        <i className="fa-solid fa-arrow-down-a-z"></i>
                                                    )}
                                            </th>
                                            <th onClick={() => sortData("Status")}>
                                                {t('home.status')} {sortConfig.key === "Status" ?
                                                    (sortConfig.direction === "asc" ?
                                                        (<i className="fas fa-arrow-up" />) :
                                                        (<i className="fas fa-arrow-down" />)
                                                    ) : (
                                                        <i className="fa-solid fa-arrow-down-a-z"></i>
                                                    )}
                                            </th>
                                            <th onClick={() => sortData("ConnectedVessel")}>
                                                {t('home.connectedVessel')} {sortConfig.key === "ConnectedVessel" ?
                                                    (sortConfig.direction === "asc" ?
                                                        (<i className="fas fa-arrow-up" />) :
                                                        (<i className="fas fa-arrow-down" />)
                                                    ) : (
                                                        <i className="fa-solid fa-arrow-down-a-z"></i>
                                                    )}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentData.map((row, index) => (
                                            <tr key={index}>
                                                <td>{row.ID}</td>
                                                <td>{row.Name}</td>
                                                <td>
                                                    <span className={`status status-${row.Status2}`}>
                                                        {row.Status}
                                                    </span>
                                                </td>
                                                <td>{row.ConnectedVessel}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="pagination">
                                <button className="btn btn-secondary" onClick={handlePreviousPage} disabled={currentPage === 1}>
                                    <i className="fas fa-chevron-left"></i> {t('home.previous')}
                                </button>
                                <div>
                                    <span>{t('home.page')} {currentPage} {t('home.of')} {totalPage},</span>
                                    <div className="pagination-controls">
                                        <span>{t('home.rowsPerPage')}:</span>
                                        <select
                                            className="form-control rows-select"
                                            value={
                                                itemsPerPage === dataGridPoint.length && ![5, 10, 15, 50].includes(itemsPerPage)
                                                    ? "All"
                                                    : itemsPerPage
                                            }
                                            onChange={(e) => {
                                                const value = e.target.value === "All" ? dataGridPoint.length : parseInt(e.target.value, 10);
                                                setItemsPerPage(value);
                                                setCurrentPage(1);
                                            }}
                                        >
                                            <option value="5">5</option>
                                            <option value="10">10</option>
                                            <option value="15">15</option>
                                            <option value="50">50</option>
                                            <option value="All">{t('home.all')}</option>
                                        </select>
                                    </div>
                                </div>
                                <button
                                    className="btn btn-secondary"
                                    onClick={handleNextPage}
                                    disabled={currentPage === Math.ceil(dataGridPoint.length / itemsPerPage)}
                                >
                                    {t('home.next')} <i className="fas fa-chevron-right"></i>
                                </button>
                            </div>
                        </div>

                        <div className='card incidents'>
                            <div className='caption'>
                                <span className='title'>{t('home.recentIncidents')}</span>
                            </div>
                            <div className='list'>
                                <div className='buttons' >
                                    <button className={`btn ${showResolved ? '' : 'active'}`} onClick={() => setShowResolved(false)}>
                                        {t('home.open')}
                                    </button>
                                    <button className={`btn ${showResolved ? 'active' : ''}`} onClick={() => setShowResolved(true)}>
                                        {t('home.closed')}
                                    </button>
                                </div>

                                {showResolved ? (
                                    <div className='detail'>
                                        <ul>
                                            {dataIncidentsClose.map((incident) => (
                                                <li key={incident.ID}>
                                                    <i className="fas fa-exclamation-triangle"></i>
                                                    {incident.Description}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ) : (
                                    <div className='detail'>
                                        <ul>
                                            {dataIncidentsOpen.map((incident) => (
                                                <li key={incident.ID}>
                                                    <i className="fas fa-exclamation-triangle"></i>
                                                    {incident.Description}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default HomeComponent;